/**
 * When the "arriving" alert with the Vello arrival sound is told (D711, owner idea 2026-10-24, from Mercado Livre's doorbell + voice) - governed key
 * `notification.arrivalAlert`.
 *
 * Shape: `{ enabled, withinMeters, withinSeconds }`:
 *
 *  - `enabled`: the market tells the rider (and a Food customer) that the driver / the courier is about to arrive, with the short brand sound. ON where nothing governs;
 *  - `withinMeters`: the straight-line distance (metres) from the driver's or the courier's fix to the pickup / drop-off point at which the alert is due
 *    ({@link ARRIVAL_ALERT_BOUNDS}: 50 to 5 000);
 *  - `withinSeconds`: the estimated time (seconds) at which it is due ({@link ARRIVAL_ALERT_BOUNDS}: 10 to 900). Whichever of the two comes FIRST decides;
 *  - `estimate` (OPTIONAL, D713): how the server estimates that time from a fix - `{ speedMetersPerMinute, detourPercent, maxFixAgeSeconds }`, the straight line
 *    stretched by the detour over the nominal speed, and the oldest fix it trusts (integers: a governed value holds safe integers only) ({@link ARRIVAL_ESTIMATE_BOUNDS}). A version without it (every version published before
 *    D713) reads {@link DEFAULT_ARRIVAL_ESTIMATE} ({@link arrivalEstimateOf}).
 *
 * The alert is told ONCE per ride or order (the notifier claims it per subject and kind). A notification rule, not a fare, dispatch or safety decision: country, state
 * and market scope, OVERRIDABLE (a lower scope may widen or narrow it), under four eyes on every change, versioned, effective-dated and content-hashed. An UNSET market
 * reads {@link DEFAULT_ARRIVAL_ALERT} (on, 500 m / 60 s - the owner's numbers); a version the reader cannot verify or parse reads the strictest thing (off) with
 * `ARRIVAL_ALERT_INVALID` - never a looser one. Distances are metres and times seconds (ADR-0007); no country, unit or language is named here.
 */
import { policySetting, resolvePolicy } from './policy.js';
import { settingSnapshotFields } from './setting-kernel.js';
import { SCOPE_REF_KIND, marketSettingContext, settingScopeRefIn, settingScopeRefValid } from './setting-context.js';
const intIn = (v, min, max) => typeof v === 'number' && Number.isSafeInteger(v) && v >= min && v <= max;
/** The governed key. */
export const ARRIVAL_ALERT_KEY = 'notification.arrivalAlert';
/** The hard bounds of an estimate (the migration mirrors them). */
export const ARRIVAL_ESTIMATE_BOUNDS = {
    speedMetersPerMinute: { min: 60, max: 2_400 },
    detourPercent: { min: 100, max: 300 },
    maxFixAgeSeconds: { min: 10, max: 600 },
};
/** The estimate every market used before D713: ~30 km/h (500 m/min, the tracking ETA's nominal speed), a 30 % detour, a fix up to two minutes old. */
export const DEFAULT_ARRIVAL_ESTIMATE = { speedMetersPerMinute: 500, detourPercent: 130, maxFixAgeSeconds: 120 };
const ESTIMATE_MEMBERS = ['speedMetersPerMinute', 'detourPercent', 'maxFixAgeSeconds'];
/** The stable issue of a value that is not an estimate, or undefined when it is one. */
export function arrivalEstimateIssue(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return 'ESTIMATE_NOT_AN_OBJECT';
    const o = v;
    if (Object.keys(o).some((k) => !ESTIMATE_MEMBERS.includes(k)) || ESTIMATE_MEMBERS.some((m) => !(m in o)))
        return 'ESTIMATE_UNKNOWN_OR_MISSING_MEMBER';
    const b = ARRIVAL_ESTIMATE_BOUNDS;
    if (!intIn(o.speedMetersPerMinute, b.speedMetersPerMinute.min, b.speedMetersPerMinute.max))
        return 'INVALID_ESTIMATE_SPEED';
    if (!intIn(o.detourPercent, b.detourPercent.min, b.detourPercent.max))
        return 'INVALID_ESTIMATE_DETOUR_PERCENT';
    if (!intIn(o.maxFixAgeSeconds, b.maxFixAgeSeconds.min, b.maxFixAgeSeconds.max))
        return 'INVALID_ESTIMATE_MAX_FIX_AGE_SECONDS';
    return undefined;
}
export const copyArrivalEstimate = (e) => ({ speedMetersPerMinute: e.speedMetersPerMinute, detourPercent: e.detourPercent, maxFixAgeSeconds: e.maxFixAgeSeconds });
/** Pure: the estimated seconds to a point `distanceMeters` away (straight line) under `estimate`. */
export function estimatedSecondsTo(distanceMeters, estimate) {
    return Math.round((Math.max(0, distanceMeters) * estimate.detourPercent * 60) / (100 * estimate.speedMetersPerMinute));
}
/** The estimate a value decides by (its own, or the pre-D713 default when it carries none). */
export function arrivalEstimateOf(value) {
    return value.estimate ?? DEFAULT_ARRIVAL_ESTIMATE;
}
/** The hard bounds the floor enforces (the migration mirrors them). */
export const ARRIVAL_ALERT_BOUNDS = {
    withinMeters: { min: 50, max: 5_000 },
    withinSeconds: { min: 10, max: 900 },
};
/** What an ungoverned market reads: on, 500 m or about a minute, whichever comes first. */
export const DEFAULT_ARRIVAL_ALERT = { enabled: true, withinMeters: 500, withinSeconds: 60 };
/** What an unreadable version reads: the strictest thing - no alert. */
export const STRICT_ARRIVAL_ALERT = { enabled: false, withinMeters: ARRIVAL_ALERT_BOUNDS.withinMeters.min, withinSeconds: ARRIVAL_ALERT_BOUNDS.withinSeconds.min };
export const ARRIVAL_ALERT_REASON_CODES = ['ARRIVAL_ALERT_GOVERNED', 'ARRIVAL_ALERT_DEFAULT', 'ARRIVAL_ALERT_INVALID'];
/** The stable codes of one decision over a fix (why the alert is, or is not, due now). */
export const ARRIVAL_ALERT_DECISION_CODES = [
    'ARRIVAL_ALERT_WITHIN_DISTANCE',
    'ARRIVAL_ALERT_WITHIN_ETA',
    'ARRIVAL_ALERT_TOO_FAR',
    'ARRIVAL_ALERT_STALE_FIX',
    'ARRIVAL_ALERT_DISABLED',
];
const MARKET_ID = /^[a-z]{2}:[a-z0-9]+(?:-[a-z0-9]+)*$/;
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
/** The stable issue of a value that is not an arrival-alert value, or undefined when it is one. */
export function arrivalAlertIssue(v) {
    if (!isObj(v))
        return 'NOT_AN_OBJECT';
    const keys = Object.keys(v);
    if (keys.some((k) => k !== 'enabled' && k !== 'withinMeters' && k !== 'withinSeconds' && k !== 'estimate') || !('enabled' in v) || !('withinMeters' in v) || !('withinSeconds' in v))
        return 'UNKNOWN_OR_MISSING_MEMBER';
    if (typeof v.enabled !== 'boolean')
        return 'INVALID_ENABLED';
    if (!intIn(v.withinMeters, ARRIVAL_ALERT_BOUNDS.withinMeters.min, ARRIVAL_ALERT_BOUNDS.withinMeters.max))
        return 'INVALID_WITHIN_METERS';
    if (!intIn(v.withinSeconds, ARRIVAL_ALERT_BOUNDS.withinSeconds.min, ARRIVAL_ALERT_BOUNDS.withinSeconds.max))
        return 'INVALID_WITHIN_SECONDS';
    if ('estimate' in v)
        return arrivalEstimateIssue(v.estimate);
    return undefined;
}
export function validArrivalAlertValue(v) {
    return arrivalAlertIssue(v) === undefined;
}
const copyArrivalAlert = (v) => ({
    enabled: v.enabled,
    withinMeters: v.withinMeters,
    withinSeconds: v.withinSeconds,
    ...(v.estimate !== undefined ? { estimate: copyArrivalEstimate(v.estimate) } : {}),
});
/** `notification.arrivalAlert@<scope>:<ref>#v<n>`. */
export function arrivalAlertVersionOf(scope, scopeRef, version) {
    return `${ARRIVAL_ALERT_KEY}@${scope}:${scopeRef}#v${version}`;
}
/** Build a valid version (country, state or market scope). Throws a RangeError on a malformed scope ref or value. */
export function arrivalAlertSetting(input) {
    if (!settingScopeRefValid(input.scope, input.scopeRef)) {
        throw new RangeError(`a ${input.scope}-scope arrival alert version needs a valid ${SCOPE_REF_KIND[input.scope]}, got ${input.scopeRef}`);
    }
    const issue = arrivalAlertIssue(input.value);
    if (issue !== undefined)
        throw new RangeError(`invalid arrival alert: ${issue}`);
    return policySetting({
        key: ARRIVAL_ALERT_KEY,
        scope: input.scope,
        scopeRef: input.scopeRef,
        value: copyArrivalAlert(input.value),
        version: input.version,
        effectiveFrom: input.effectiveFrom,
        ...(input.effectiveTo !== undefined ? { effectiveTo: input.effectiveTo } : {}),
        mode: 'OVERRIDABLE',
    });
}
/**
 * What `marketId` decides about the arrival alert at `at`, over the GOVERNED reader's versions: the most specific of the country, the state and the market decides.
 * Nothing governing reads the default (on, 500 m / 60 s); a version that cannot be read or parsed reads the strictest (off). `regionCode` is the market's registered
 * state (ADR-0124 - a market with none skips the layer).
 */
export function resolveArrivalAlert(settings, marketId, at, regionCode) {
    const strict = { marketId, value: STRICT_ARRIVAL_ALERT, reasonCode: 'ARRIVAL_ALERT_INVALID', policyVersion: `${ARRIVAL_ALERT_KEY}@invalid` };
    if (!MARKET_ID.test(marketId))
        return strict;
    const ctx = marketSettingContext(marketId, regionCode);
    const country = ctx.country;
    const relevant = settings.filter((s) => s.key === ARRIVAL_ALERT_KEY && (s.scope === 'country' || s.scope === 'region' || s.scope === 'market'));
    const resolution = resolvePolicy(relevant, ctx, ARRIVAL_ALERT_KEY, at);
    if (resolution.reason === 'NOT_SET') {
        return { marketId, value: DEFAULT_ARRIVAL_ALERT, reasonCode: 'ARRIVAL_ALERT_DEFAULT', policyVersion: `${ARRIVAL_ALERT_KEY}@default` };
    }
    if (resolution.scope === undefined || resolution.version === undefined || (resolution.scope !== 'country' && resolution.scope !== 'region' && resolution.scope !== 'market'))
        return strict;
    if (!validArrivalAlertValue(resolution.value))
        return strict;
    const ref = settingScopeRefIn(resolution.scope, ctx) ?? country;
    const snap = settingSnapshotFields(relevant, ARRIVAL_ALERT_KEY, resolution.scope, ref, resolution.version);
    return {
        marketId,
        value: copyArrivalAlert(resolution.value),
        reasonCode: 'ARRIVAL_ALERT_GOVERNED',
        policyVersion: arrivalAlertVersionOf(resolution.scope, ref, resolution.version),
        ...(snap.contentHash !== undefined ? { contentHash: snap.contentHash } : {}),
        ...(snap.changeId !== undefined ? { changeId: snap.changeId } : {}),
    };
}
export function arrivalAlertWire(decision) {
    return { enabled: decision.value.enabled, withinMeters: decision.value.withinMeters, withinSeconds: decision.value.withinSeconds, policyVersion: decision.policyVersion };
}
/**
 * Pure: whether a fix `distanceMeters` (straight line) and an estimated `etaSeconds` from the point make the alert due under `value` - the distance first, then the
 * time, whichever is met first; never on a fix older than `maxFixAgeSeconds` (a stale fix says nothing about now), never while the market turned it off.
 */
export function arrivalAlertDue(input, value) {
    if (!value.enabled)
        return { due: false, code: 'ARRIVAL_ALERT_DISABLED' };
    if (input.fixAgeSeconds !== undefined && input.maxFixAgeSeconds !== undefined && input.fixAgeSeconds > input.maxFixAgeSeconds)
        return { due: false, code: 'ARRIVAL_ALERT_STALE_FIX' };
    if (input.distanceMeters <= value.withinMeters)
        return { due: true, code: 'ARRIVAL_ALERT_WITHIN_DISTANCE' };
    if (input.etaSeconds <= value.withinSeconds)
        return { due: true, code: 'ARRIVAL_ALERT_WITHIN_ETA' };
    return { due: false, code: 'ARRIVAL_ALERT_TOO_FAR' };
}
//# sourceMappingURL=arrival-alert-policy.js.map